import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import ConfirmModal from '../../components/ConfirmModal';
import { fmtDate } from '../../utils/formatUtils';
import {
  loggerLabel, oefeningenVan, sessieDatum, vatOefeningSamen,
} from '../../utils/workoutHistoryUtils';
import { describeError, errorText } from '../../utils/errorMessages';
import { t } from '../../i18n/nl.js';

// ─────────────────────────────────────────────────────────────────────────
// Onderdeel 6 — trainingsgeschiedenis voor de cliënt in de webportal.
//
// Inhoudelijk gelijk aan de mobiele geschiedenis (zelfde samenvattings-
// helpers uit `workoutHistoryUtils.js`, zelfde badges, zelfde
// "gelogd door"-logica), zodat een cliënt op beide kanalen hetzelfde leest.
//
// Rechten: een zelfgelogde sessie mag de cliënt bekijken, bewerken (datum +
// opmerking) en verwijderen; een door de coach gelogde sessie is
// alleen-lezen. De knoppen volgen die regel, maar zijn niet de beveiliging:
// `workout_sessions_update`/`_delete` (RLS) laat een cliënt sowieso alleen
// aan eigen rijen met `logged_by = 'client'` komen.
// ─────────────────────────────────────────────────────────────────────────

const PAGINA_GROOTTE = 10;

// Selectie in één geneste call — sessie → oefeningen → (oefening + sets).
// Geen query per sessie of per oefening.
const SESSIE_SELECT = `
  id, performed_on, completed_at, created_at, notes, coach_id, logged_by,
  template:client_exercise_templates(id, name),
  workout_session_exercises(id, sort_order, coach_note, exercise_type,
    exercises(id, name, exercise_type),
    workout_sets(id, set_number, reps, weight, rpe,
      duration_seconds, distance_meters, speed_value, incline_percent,
      resistance_level, heart_rate_avg, calories))
`;

function vandaagStr() {
  const nu = new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${nu.getFullYear()}-${pad(nu.getMonth() + 1)}-${pad(nu.getDate())}`;
}

export default function TrainingslogboekPage() {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();

  const [sessies, setSessies] = useState([]);
  const [coaches, setCoaches] = useState({});
  const [totaal, setTotaal] = useState(0);
  const [limiet, setLimiet] = useState(PAGINA_GROOTTE);
  const [loading, setLoading] = useState(true);
  const [laadFout, setLaadFout] = useState(null);
  const [uitgeklapt, setUitgeklapt] = useState({});

  const [bewerkId, setBewerkId] = useState(null);
  const [bewerkDatum, setBewerkDatum] = useState('');
  const [bewerkNotitie, setBewerkNotitie] = useState('');
  const [opslaan, setOpslaan] = useState(false);
  const [verwijderDoel, setVerwijderDoel] = useState(null);
  const [mijnNaam, setMijnNaam] = useState(null);

  const clientId = user?.id;

  // Eigen naam apart ophalen: de web-AuthContext houdt alleen sessie en rol
  // bij, geen profiel. Nodig voor "Gelogd door <naam>" bij een zelfgelogde
  // sessie (Onderdeel 7).
  useEffect(() => {
    if (!clientId) return;
    supabase.from('profiles').select('full_name').eq('id', clientId).maybeSingle()
      .then(({ data }) => setMijnNaam(data?.full_name || null));
  }, [clientId]);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setLaadFout(null);
    try {
      const { data, error, count } = await supabase
        .from('workout_sessions')
        .select(SESSIE_SELECT, { count: 'exact' })
        .eq('client_id', clientId)
        .not('completed_at', 'is', null)
        // Onderdeel 1: de echte trainingsdatum, niet de aanmaakdatum.
        .order('performed_on', { ascending: false })
        .order('id', { ascending: false })
        .limit(limiet);
      if (error) throw error;

      const rijen = data || [];
      setSessies(rijen);
      setTotaal(count ?? rijen.length);

      // Coachnamen via de kolom-veilige view, in één query (geen N+1).
      const coachIds = [...new Set(rijen.map(r => r.coach_id).filter(Boolean))];
      if (coachIds.length) {
        const { data: profielen, error: profielErr } = await supabase
          .from('coach_basic_profiles')
          .select('id, full_name')
          .in('id', coachIds);
        if (profielErr) throw profielErr;
        setCoaches(Object.fromEntries((profielen || []).map(p => [p.id, p.full_name])));
      } else {
        setCoaches({});
      }
    } catch (err) {
      setLaadFout(describeError(err, { scope: 'TrainingslogboekPage.load' }));
    } finally {
      setLoading(false);
    }
  }, [clientId, limiet]);

  useEffect(() => { load(); }, [load]);

  function startBewerken(sessie) {
    setBewerkId(sessie.id);
    setBewerkDatum(sessie.performed_on || '');
    setBewerkNotitie(sessie.notes || '');
  }

  async function slaOp(sessieId) {
    setOpslaan(true);
    try {
      const { data, error } = await supabase
        .from('workout_sessions')
        .update({ performed_on: bewerkDatum || null, notes: bewerkNotitie.trim() || null })
        .eq('id', sessieId)
        .select('id')
        .maybeSingle();
      if (error) throw error;
      // PostgREST geeft geen fout als RLS de rij wegfiltert — dan raakt de
      // update 0 rijen en zou de UI ten onrechte "opgeslagen" melden.
      if (!data) throw Object.assign(new Error('rij niet geraakt'), { code: '42501' });
      setBewerkId(null);
      showSuccess(t('clientTraining.opgeslagen'));
      await load();
    } catch (err) {
      showError(errorText(err, { scope: 'TrainingslogboekPage.opslaan' }));
    } finally {
      setOpslaan(false);
    }
  }

  async function verwijder() {
    const sessie = verwijderDoel;
    setVerwijderDoel(null);
    try {
      const { data, error } = await supabase
        .from('workout_sessions')
        .delete()
        .eq('id', sessie.id)
        .select('id')
        .maybeSingle();
      if (error) throw error;
      if (!data) throw Object.assign(new Error('rij niet geraakt'), { code: '42501' });
      showSuccess(t('clientTraining.verwijderd'));
      await load();
    } catch (err) {
      showError(errorText(err, { scope: 'TrainingslogboekPage.verwijderen' }));
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 className="page-title">{t('clientTraining.titel')}</h1>
        <p className="page-subtitle">{t('clientTraining.subtitel')}</p>
      </div>

      {laadFout && (
        <div className="error-msg" style={{ marginBottom: 16 }}>
          {laadFout.message} {t('errors.referenceLabel', { reference: laadFout.reference })}
        </div>
      )}

      {loading && sessies.length === 0 ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : sessies.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div style={{ fontSize: 32 }}>🏋️</div>
            <p style={{ fontWeight: 600 }}>{t('clientTraining.leegTitel')}</p>
            <p className="text-muted" style={{ maxWidth: 460, margin: '4px auto 0' }}>
              {t('clientTraining.leegTekst')}
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="workout-sessions">
            {sessies.map(sessie => {
              const oefeningen = oefeningenVan(sessie);
              const eigen = sessie.logged_by === 'client';
              const open = !!uitgeklapt[sessie.id];
              const inBewerking = bewerkId === sessie.id;
              return (
                <article key={sessie.id} className="workout-session">
                  <div className="workout-session-head">
                    <h3>{fmtDate(sessieDatum(sessie))}</h3>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <span className="badge badge-active">
                        {sessie.template?.name || t('trainingHistory.losseSessie')}
                      </span>
                      <span className={`badge ${eigen ? 'badge-zelfstandig' : 'badge-coach-log'}`}>
                        {eigen ? t('trainingHistory.badgeZelfstandig') : t('trainingHistory.badgeDoorCoach')}
                      </span>
                    </div>
                  </div>
                  <p className="workout-session-meta">
                    {loggerLabel(sessie, coaches, mijnNaam)}
                  </p>

                  {oefeningen.length === 0 ? (
                    <p className="workout-session-meta">{t('trainingHistory.geenOefeningen')}</p>
                  ) : (
                    <ul className="workout-session-oefeningen">
                      {(open ? oefeningen : oefeningen.slice(0, 3)).map(oefening => (
                        <li key={oefening.id}>
                          <div>
                            <span className="workout-session-oefening-naam">{oefening.exercises?.name}</span>
                            {oefening.sets.length > 0 && (
                              <span className="workout-session-oefening-sets">{vatOefeningSamen(oefening)}</span>
                            )}
                          </div>
                          {oefening.coach_note && (
                            <span className="workout-session-oefening-note">
                              {t('trainingHistory.aandachtspunt', { tekst: oefening.coach_note })}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}

                  {oefeningen.length > 3 && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setUitgeklapt(prev => ({ ...prev, [sessie.id]: !prev[sessie.id] }))}
                    >
                      {open ? t('clientTraining.minderTonen') : t('clientTraining.alleOefeningen', { aantal: oefeningen.length })}
                    </button>
                  )}

                  {inBewerking ? (
                    <div className="card" style={{ marginTop: 12, padding: 12 }}>
                      <div className="form-group">
                        <label className="form-label" htmlFor={`datum-${sessie.id}`}>
                          {t('clientTraining.veldDatum')}
                        </label>
                        <input
                          id={`datum-${sessie.id}`}
                          type="date"
                          className="form-input"
                          value={bewerkDatum}
                          max={vandaagStr()}
                          onChange={e => setBewerkDatum(e.target.value)}
                        />
                        <small className="text-muted">{t('clientTraining.veldDatumHint')}</small>
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor={`notitie-${sessie.id}`}>
                          {t('clientTraining.veldNotitie')}
                        </label>
                        <textarea
                          id={`notitie-${sessie.id}`}
                          className="form-input"
                          rows={3}
                          value={bewerkNotitie}
                          onChange={e => setBewerkNotitie(e.target.value)}
                          style={{ resize: 'vertical' }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setBewerkId(null)}>
                          {t('clientTraining.annuleren')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          disabled={opslaan}
                          onClick={() => slaOp(sessie.id)}
                        >
                          {opslaan ? '…' : t('clientTraining.opslaan')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {sessie.notes && (
                        <blockquote className="workout-session-notitie">{sessie.notes}</blockquote>
                      )}
                      {eigen ? (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => startBewerken(sessie)}>
                            {t('clientTraining.bewerken')}
                          </button>
                          <button type="button" className="btn btn-danger btn-sm" onClick={() => setVerwijderDoel(sessie)}>
                            {t('clientTraining.verwijderen')}
                          </button>
                        </div>
                      ) : (
                        <p className="text-muted" style={{ fontSize: 12, marginTop: 8 }}>
                          {t('clientTraining.alleenLezen')}
                        </p>
                      )}
                    </>
                  )}
                </article>
              );
            })}
          </div>

          <div className="workout-sessions-footer">
            <span className="text-muted" style={{ fontSize: 12 }}>
              {t('trainingHistory.aantalGetoond', { getoond: sessies.length, totaal })}
            </span>
            {sessies.length < totaal && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={loading}
                onClick={() => setLimiet(huidig => huidig + PAGINA_GROOTTE)}
              >
                {t('trainingHistory.meerLaden')}
              </button>
            )}
          </div>
        </>
      )}

      {verwijderDoel && (
        <ConfirmModal
          title={t('clientTraining.verwijderTitel')}
          body={t('clientTraining.verwijderTekst', { datum: fmtDate(sessieDatum(verwijderDoel)) })}
          confirmLabel={t('clientTraining.verwijderBevestig')}
          danger
          onConfirm={verwijder}
          onClose={() => setVerwijderDoel(null)}
        />
      )}
    </div>
  );
}
